import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Shield, UserPlus, Trash2, AlertTriangle, Loader2, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { Badge } from "@/components/ui/badge";
import { listAdminsServer, addAdminServer, removeAdminServer } from "@/server/admin";
import { logError } from "@/lib/structured-log";
import { extractApiErrorMessage } from "@/lib/api/error";
import type { AdminUserResponse } from "@/lib/api/generated";

export const Route = createFileRoute("/admin/admins")({
  component: AdminManagementPage,
  loader: async () => {
    const response = await listAdminsServer();
    return { admins: response };
  },
});

function AdminManagementPage() {
  const { t } = useTranslation("admin");
  const { admins } = Route.useLoaderData();

  const [adminList, setAdminList] = useState<AdminUserResponse[]>(admins.items || []);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [adminToRemove, setAdminToRemove] = useState<AdminUserResponse | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // Refresh admin list when dialog closes
  const refreshAdmins = async () => {
    try {
      const response = await listAdminsServer();
      setAdminList(response.items || []);
    } catch (e) {
      logError("admin.refresh_admins.failed", { error: String(e) });
    }
  };

  const handleRemoveAdmin = async (force = false) => {
    if (!adminToRemove) return;

    setIsRemoving(true);
    try {
      await removeAdminServer({
        data: { userId: adminToRemove.id, force },
      });
      toast.success(t("admins.removeSuccess"));
      await refreshAdmins();
    } catch (e) {
      const message = extractApiErrorMessage(e, t("admins.removeError"));
      toast.error(message);
    } finally {
      setIsRemoving(false);
      setAdminToRemove(null);
    }
  };

  const currentAdminCount = adminList.length;
  const isLastAdmin = currentAdminCount <= 1;

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-6 py-4">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold tracking-tight">{t("admins.title")}</h2>
          <p className="text-sm text-muted-foreground">{t("admins.description")}</p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          {t("admins.addButton")}
        </Button>
      </div>

      {/* Admin List */}
      <div className="flex-1 overflow-auto p-6">
        {adminList.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <Shield className="mb-4 h-12 w-12 text-muted-foreground/50" />
            <h3 className="text-lg font-medium">{t("admins.empty.title")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("admins.empty.description")}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {adminList.map((admin) => (
              <AdminCard key={admin.id} admin={admin} onRemove={() => setAdminToRemove(admin)} />
            ))}
          </div>
        )}
      </div>

      {/* Add Admin Dialog */}
      <AddAdminDialog
        open={isAddDialogOpen}
        onClose={() => {
          setIsAddDialogOpen(false);
          refreshAdmins();
        }}
      />

      {/* Remove Admin Confirmation Dialog */}
      <Dialog
        open={!!adminToRemove}
        onOpenChange={(open: boolean) => !open && setAdminToRemove(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              {t("admins.removeDialog.title")}
            </DialogTitle>
            <DialogDescription className="space-y-3">
              <p>
                {t("admins.removeDialog.description", {
                  email: adminToRemove?.email || adminToRemove?.clerk_user_id,
                })}
              </p>
              {isLastAdmin && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                  <strong>{t("admins.removeDialog.lastAdminWarning")}</strong>
                  <p className="mt-1">{t("admins.removeDialog.lastAdminDescription")}</p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdminToRemove(null)} disabled={isRemoving}>
              {t("admins.removeDialog.cancel")}
            </Button>
            <Button
              onClick={() => handleRemoveAdmin(isLastAdmin)}
              disabled={isRemoving}
              variant="destructive"
            >
              {isRemoving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              {isLastAdmin ? t("admins.removeDialog.forceRemove") : t("admins.removeDialog.remove")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminCard({ admin, onRemove }: { admin: AdminUserResponse; onRemove: () => void }) {
  const { t } = useTranslation("admin");

  const fullName = useMemo(() => {
    if (admin.first_name || admin.last_name) {
      return `${admin.first_name || ""} ${admin.last_name || ""}`.trim();
    }
    return null;
  }, [admin.first_name, admin.last_name]);

  const grantedDate = useMemo(() => {
    if (!admin.granted_at) return "";
    return new Date(admin.granted_at).toLocaleDateString();
  }, [admin.granted_at]);

  return (
    <div className="flex items-center justify-between rounded-xl border bg-card p-4 hover:bg-accent/50 transition-colors">
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
          <Crown className="h-5 w-5 text-primary" />
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{fullName || admin.email || admin.clerk_user_id}</span>
            <Badge variant="secondary" className="text-xs">
              {t("admins.badge.admin")}
            </Badge>
          </div>
          <div className="text-sm text-muted-foreground space-x-3">
            {admin.email && <span>{admin.email}</span>}
            {admin.email && admin.clerk_user_id && (
              <span className="text-muted-foreground/50">•</span>
            )}
            {admin.clerk_user_id && (
              <span className="font-mono text-xs">{admin.clerk_user_id}</span>
            )}
          </div>
          {grantedDate && (
            <div className="text-xs text-muted-foreground/70">
              {t("admins.grantedAt", { date: grantedDate })}
            </div>
          )}
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onRemove}
        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
      >
        <Trash2 className="mr-2 h-4 w-4" />
        {t("admins.removeButton")}
      </Button>
    </div>
  );
}

function AddAdminDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation("admin");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formSchema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .min(1, t("admins.addDialog.validation.required"))
          .email(t("admins.addDialog.validation.invalid")),
      }),
    [t],
  );

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    setIsSubmitting(true);
    try {
      const result = await addAdminServer({
        data: { email: values.email.trim() },
      });

      if (result.success) {
        toast.success(result.message);
        form.reset();
        onClose();
      } else {
        toast.error(result.message);
      }
    } catch (e) {
      const message = extractApiErrorMessage(e, t("admins.addDialog.error"));
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            {t("admins.addDialog.title")}
          </DialogTitle>
          <DialogDescription>{t("admins.addDialog.description")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("admins.addDialog.label")}</label>
            <Input
              {...form.register("email")}
              type="email"
              placeholder={t("admins.addDialog.placeholder")}
              disabled={isSubmitting}
            />
            {form.formState.errors.email && (
              <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              {t("admins.addDialog.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t("admins.addDialog.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
